import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { TRISTATE } from "./placementsShared";
import { useLang, translateFr as tfr } from "@/i18n";

const inputCls = "bg-background/60 border-border dark:border-border";

export function AddFollowUpDialog({ open, onOpenChange, placementId, onSaved }) {
  const { tf } = useLang();
  const [label, setLabel] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim() || !date) { toast.error(tf("Libellé et date requis")); return; }
    setSaving(true);
    try {
      await api.post(`/placements/${placementId}/followups`, { label: label.trim(), scheduled_at: date, notes });
      toast.success(tf("Suivi planifié"));
      setLabel(""); setDate(""); setNotes("");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="add-followup-dialog">
        <DialogHeader>
          <DialogTitle>{tf("Ajouter un suivi")}</DialogTitle>
          <DialogDescription>{tf("Le suivi est ajouté à la timeline et au calendrier.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Libellé")}</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className={inputCls}
              placeholder={tf("Ex. : Point avec le staff")} data-testid="fu-label" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Date")}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="fu-date" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="fu-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="fu-save">
            {saving ? tf("Enregistrement…") : tf("Planifier le suivi")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TriPicker({ label, value, onChange, testid }) {
  const { tf } = useLang();
  return (
    <div>
      <div className="text-xs font-medium mb-1.5">{label}</div>
      <div className="flex gap-1.5" data-testid={testid}>
        {TRISTATE.map((t) => (
          <button key={t.id} onClick={() => onChange(value === t.id ? null : t.id)}
            data-testid={`${testid}-${t.id}`}
            className={cn("flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors",
              value === t.id ? t.cls : "border-border dark:border-border text-muted-foreground hover:text-foreground")}>
            {tf(t.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CompleteFollowUpDialog({ open, onOpenChange, placementId, followup, onSaved }) {
  const { tf } = useLang();
  const [sporting, setSporting] = useState(null);
  const [clubRel, setClubRel] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [notes, setNotes] = useState("");
  const [nextLabel, setNextLabel] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) { setSporting(null); setClubRel(null); setSatisfaction(null); setNotes(""); setNextLabel(""); setNextDate(""); }
  }, [open, followup?.id]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/placements/${placementId}/followups/${followup.id}/complete`, {
        sporting_state: sporting, club_relationship_state: clubRel, athlete_satisfaction_state: satisfaction,
        notes, next_action_label: nextLabel.trim() || null, next_action_date: nextDate || null,
      });
      toast.success(tf("Suivi enregistré"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setSaving(false); }
  };

  if (!followup) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[88vh] overflow-y-auto" data-testid="complete-followup-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wide text-sm">{followup.label}</DialogTitle>
          <DialogDescription>{tf("Comment évolue la situation ?")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <TriPicker label={tf("Situation sportive")} value={sporting} onChange={setSporting} testid="tri-sporting" />
          <TriPicker label={tf("Relation avec le club")} value={clubRel} onChange={setClubRel} testid="tri-club" />
          <TriPicker label={tf("Satisfaction de l'athlète")} value={satisfaction} onChange={setSatisfaction} testid="tri-satisfaction" />
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="cf-notes" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Prochaine action")}</Label>
              <Input value={nextLabel} onChange={(e) => setNextLabel(e.target.value)} className={inputCls}
                placeholder={tf("Optionnel")} data-testid="cf-next-label" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">{tf("Date de l'action")}</Label>
              <Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className={inputCls} data-testid="cf-next-date" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={saving} className="bg-foreground hover:bg-foreground/90 text-background" data-testid="cf-save">
            {saving ? tf("Enregistrement…") : tf("Enregistrer le suivi")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ClosePlacementDialog({ open, onOpenChange, placementId, onClosed }) {
  const { tf } = useLang();
  const [reason, setReason] = useState("contract_ended");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const reasons = [
    { id: "contract_ended", label: tfr("Contrat terminé") },
    { id: "transfer", label: "Transfert" },
    { id: "termination", label: tfr("Résiliation") },
    { id: "new_opportunity", label: tfr("Nouvelle opportunité") },
    { id: "other", label: "Autre" },
  ];

  const close = async () => {
    setSaving(true);
    try {
      await api.post(`/placements/${placementId}/close`, { reason, notes });
      toast.success(tf("Placement clôturé"));
      onOpenChange(false);
      onClosed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Erreur"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="close-placement-dialog">
        <DialogHeader>
          <DialogTitle>{tf("Clôturer le placement")}</DialogTitle>
          <DialogDescription>{tf("Le placement et son historique restent consultables dans l'onglet Historique.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5" data-testid="close-reasons">
            {reasons.map((r) => (
              <label key={r.id} className={cn("flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors",
                reason === r.id ? "border-foreground/30 bg-secondary/60" : "border-border dark:border-border")}>
                <input type="radio" name="close-reason" checked={reason === r.id} onChange={() => setReason(r.id)}
                  className="accent-[#E11D2E]" data-testid={`close-reason-${r.id}`} />
                {tf(r.label)}
              </label>
            ))}
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">{tf("Notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid="close-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={close} disabled={saving} className="bg-foreground hover:bg-foreground/90 text-background" data-testid="close-confirm">
            {saving ? tf("Clôture…") : tf("Clôturer le placement")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
